import logo from '../../resources/logo_white.png';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BiBowlHot, BiUser } from 'react-icons/bi';
import { RiShoppingBasket2Fill } from 'react-icons/ri';
import { MdMenuBook, MdOutlineAttachMoney, MdOutlineEventNote } from 'react-icons/md';
import { BsBoxes, BsWallet2 } from 'react-icons/bs';
import { AiOutlineLineChart, AiOutlineDoubleLeft, AiOutlineDoubleRight } from 'react-icons/ai';
import { useAuth } from '../../auth/AuthenticationWrapper';

const SideMenu = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showText, setShowText] = useState(false);
    const [clientLogin, setClientLogin] = useState("");
    const navigate = useNavigate();
    const { user, hasPermission } = useAuth();

    const canViewOrders = hasPermission("orders", "view");
    const canViewSales = hasPermission("sales", "view");
    const canViewMenu = hasPermission("menu", "view");
    const canViewStorage = hasPermission("storage", "view");
    const canViewCustomers = hasPermission("customers", "view");
    const canViewFinancial = hasPermission("financial", "view");
    const canViewReservations = hasPermission("reservations", "view");
    const canViewWallet = hasPermission("wallet", "view");
    const asaasEnabled = user?.asaasEnabled || false;

    useEffect(() => {
        if (user?.login) {
            setClientLogin(user.login);
        }
    }, [user]);

    useEffect(() => {
        if (isMenuOpen) {
            const timer = setTimeout(() => setShowText(true), 150);
            return () => clearTimeout(timer);
        } else {
            setShowText(false);
        }
    }, [isMenuOpen]);

    const toggleMenu = () => setIsMenuOpen(prev => !prev);

    const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    let userRole = user?.role || '';
    if (userRole === 'ADMIN' || userRole === 'CASHIER' || userRole === 'ATTENDANT') {
        userRole = 'OPERATIONAL';
    }

    const handleMenuItemClick = (to, disabled) => {
        if (!disabled) {
            navigate(to);
            if (isTouchDevice()) setIsMenuOpen(false);
        }
    };

    const menuItemsByRole = {
        OPERATIONAL: [
            { icon: <BiBowlHot className="text-white scale-200 mx-10" />, color: 'bg-custom_red', title: 'Fila de Pedidos', to: '/pedidos', disabled: !canViewOrders },
            { icon: <RiShoppingBasket2Fill className="text-white scale-200 mx-10" />, color: 'bg-custom_red', title: 'Novo Pedido', to: '/vendas', disabled: !canViewSales },
            { icon: <MdOutlineEventNote className="text-white scale-200 mx-10" />, color: 'bg-custom_red', title: 'Reservas', to: '/reservas', disabled: !canViewReservations },
            { icon: <MdMenuBook className="text-white scale-200 mx-10" />, color: 'bg-custom_blue', title: 'Cardápio', to: '/cardapio', disabled: !canViewMenu },
            { icon: <BsBoxes className="text-white scale-200 mx-10" />, color: 'bg-custom_blue', title: 'Estoque', to: '/estoque', disabled: !canViewStorage },
            { icon: <BiUser className="text-white scale-200 mx-10" />, color: 'bg-custom_green', title: 'Clientes', to: '/clientes', disabled: !canViewCustomers },
            { icon: <AiOutlineLineChart className="text-white scale-200 mx-10" />, color: 'bg-custom_yellow', title: 'Financeiro', to: '/financeiro', disabled: !canViewFinancial },
            ...(asaasEnabled ? [{ icon: <BsWallet2 className="text-white scale-200 mx-10" />, color: 'bg-custom_yellow', title: 'Carteira Digital', to: '/carteira-digital', disabled: !canViewWallet }] : []),
        ],
        STUDENT: [
            { icon: <RiShoppingBasket2Fill className="text-white scale-200 mx-10" />, color: 'bg-custom_red', title: 'Nova Reserva', to: '/novacompra', disabled: false },
            { icon: <BiUser className="text-white scale-200 mx-10" />, color: 'bg-custom_green', title: 'Perfil', to: `/aluno/${clientLogin}`, disabled: !clientLogin },
        ],
        PARENT: [
            { icon: <RiShoppingBasket2Fill className="text-white scale-200 mx-10" />, color: 'bg-custom_red', title: 'Novo Pedido', to: '/novacompra/responsavel', disabled: false },
            { icon: <MdOutlineEventNote className="text-white scale-200 mx-10" />, color: 'bg-custom_red', title: 'Pedidos', to: '/reservas/responsavel', disabled: false },
            { icon: <MdMenuBook className="text-white scale-200 mx-10" />, color: 'bg-custom_blue', title: 'Cardápio', to: '/cardapio', disabled: false },
            { icon: <BiUser className="text-white scale-200 mx-10" />, color: 'bg-custom_green', title: 'Perfil', to: `/clientes/detalhesResponsavel/${clientLogin}`, disabled: false },
            { icon: <MdOutlineAttachMoney className="text-white scale-200 mx-10" />, color: 'bg-custom_yellow', title: 'Financeiro', to: `/financeiroCliente/${clientLogin}`, disabled: false },
        ],
    };

    const appVersion = process.env.REACT_APP_VERSION;

    if (!user) return null;
    return (
        <div
            className={`bg-side_menu fixed top-0 left-0 h-screen z-100 transition-all duration-300 overflow-hidden 
                ${isMenuOpen ? "w-[300px]" : "w-[100px]"}`}
            onMouseEnter={() => !isTouchDevice() && setIsMenuOpen(true)}
            onMouseLeave={() => !isTouchDevice() && setIsMenuOpen(false)}
        >
            <div className="flex flex-col h-full">
                <div className="flex flex-col items-center py-4 flex-shrink-0">
                    <img src={logo} alt='Logo Foodz' className='w-4/5' />
                    <hr className="mt-4 border-t border-custom_gray_font w-4/5" />
                    <div className="w-full flex justify-center">
                        <span className="text-xs text-custom_lighter_gray_font text-center mt-1">
                            v{appVersion}
                        </span>
                    </div>
                </div>

                {userRole && menuItemsByRole[userRole] && (
                    <div className="flex-1 overflow-y-auto w-full py-2">
                        {menuItemsByRole[userRole].map((item, index) => (
                            <button
                                key={index}
                                className={`w-full h-10 my-2 flex items-center transition-all duration-300
                                    ${item.disabled ? "bg-slate-400" : item.color}`}
                                onClick={() => handleMenuItemClick(item.to, item.disabled)}
                                disabled={item.disabled}
                            >
                                <span className="w-[100px] flex justify-center min-w-0">
                                    {item.icon}
                                </span>
                                <span
                                    className={`font-montserrat font-medium text-white text-xl transition-all duration-300
                                        ${showText ? "opacity-100 w-auto" : "opacity-0 w-0 whitespace-nowrap overflow-hidden"}`}
                                >
                                    {item.title}
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                <div className="flex-shrink-0 pb-4 px-4">
                    <div className="flex justify-end">
                        <button onClick={toggleMenu}>
                            {isMenuOpen ? (
                                <AiOutlineDoubleLeft className="text-custom_lighter_gray_font scale-150 transition-transform duration-300" />
                            ) : (
                                <AiOutlineDoubleRight className="text-custom_lighter_gray_font scale-150 transition-transform duration-300" />
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SideMenu;